import React, { useId, useState } from "react";
import ClassNames from "classnames";
import Label, { LabelVariant } from "../Label/Label";
import Tooltip from "../Tooltip/Tooltip";
import "./ChoiceCards.scss";

export interface ChoiceCardItem {
  value: string | number;
  title: string;
  subtitle: string;
  titleInfo?: string;
  titleTag?: string;
  labels?: string[];
  content: React.ReactNode;
}

export interface ChoiceCardsProps {
  cards: ChoiceCardItem[];
  defaultSelected?: ChoiceCardItem["value"];
  className?: string;
  "data-testid"?: string;
}

const ChoiceCards: React.FC<ChoiceCardsProps> = ({
  cards,
  defaultSelected,
  className = "",
  "data-testid": dataTestId,
}) => {
  const groupName = useId();
  const [selectedValue, setSelectedValue] = useState<
    ChoiceCardItem["value"] | undefined
  >(defaultSelected ?? cards[0]?.value);

  if (!cards?.length) {
    return null;
  }

  const selectedCard =
    cards.find((card) => card.value === selectedValue) ?? cards[0];

  return (
    <div
      className={ClassNames("choice-cards", className)}
      data-testid={dataTestId}
    >
      <div
        className="choice-cards-header-row"
        role="radiogroup"
        aria-label="Choice cards"
      >
        {cards.map((card) => {
          const isSelected = card.value === selectedCard.value;

          return (
            <label
              key={card.value}
              className={ClassNames("choice-card", {
                "is-selected": isSelected,
              })}
            >
              <input
                className="choice-card-radio-input"
                type="radio"
                name={groupName}
                value={card.value}
                checked={isSelected}
                onChange={() => setSelectedValue(card.value)}
                data-testid={
                  dataTestId ? `${dataTestId}-radio-${card.value}` : undefined
                }
              />
              <div className="choice-card-title-row">
                <div className="choice-card-title-group">
                  <span className="choice-card-title">{card.title}</span>
                  {!!card.titleInfo && (
                    <Tooltip type="info" content={card.titleInfo}>
                      <i className="seem-icon seem-icon-info choice-card-info-icon" />
                    </Tooltip>
                  )}
                </div>
                {!!card.titleTag && (
                  <Label
                    className="choice-card-tag"
                    text={card.titleTag}
                    variant={LabelVariant.Rect}
                  />
                )}
              </div>
              <Tooltip type="ellipsis" content={card.subtitle}>
                <span className="choice-card-subtitle">{card.subtitle}</span>
              </Tooltip>
              {!!card.labels?.length && (
                <div className="choice-card-labels">
                  {card.labels.map((label, index) => (
                    <Label
                      key={`${label}-${index}`}
                      text={label}
                      variant={LabelVariant.Outlined}
                    />
                  ))}
                </div>
              )}
            </label>
          );
        })}
      </div>
      <div className="choice-cards-body">{selectedCard.content}</div>
    </div>
  );
};

export default ChoiceCards;
